import { Bell, Check, Package, Wallet, Gift, LifeBuoy, ShoppingBag } from 'lucide-react';
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_ICONS = {
  order_paid: ShoppingBag,
  shipment: Package,
  wallet_topup: Wallet,
  admin_credit: Wallet,
  referral_reward: Gift,
  referral_cashback: Gift,
  rsa_update: LifeBuoy,
};

export default function NotificationBell() {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const seenIds = useRef(new Set());

  const fetchNotifs = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      // Trigger browser Notification for new items (if permission granted)
      if (Notification && Notification.permission === 'granted') {
        for (const n of data.items) {
          if (!n.read && !seenIds.current.has(n.id)) {
            try { new Notification(n.title, { body: n.body, icon: '/favicon.ico' }); } catch {}
          }
        }
      }
      data.items.forEach((n) => seenIds.current.add(n.id));
      setItems(data.items);
      setUnread(data.unread_count);
    } catch {}
  }, [token]);

  useEffect(() => {
    if (!token) return;
    // Seed seen ids so we don't fire browser popups for already-existing items on first mount
    (async () => {
      try {
        const { data } = await axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } });
        data.items.forEach((n) => seenIds.current.add(n.id));
        setItems(data.items);
        setUnread(data.unread_count);
      } catch {}
    })();
    const t = setInterval(fetchNotifs, 20000);
    return () => clearInterval(t);
  }, [token, fetchNotifs]);

  if (!user) return null;

  const askPermission = () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const openPanel = () => {
    setOpen((v) => !v);
    askPermission();
  };

  const markOne = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setItems(items.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread(Math.max(0, unread - 1));
    } catch {}
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setItems(items.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {}
  };

  return (
    <div className="relative">
      <button onClick={openPanel} className="btn-ghost !py-1.5 !px-3 text-sm relative">
        <Bell size={15} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold px-1">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}></div>
          <div className="absolute right-0 mt-2 w-[340px] card-soft shadow-2xl z-50 overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <p className="font-semibold">Notifications</p>
              {unread > 0 && <button onClick={markAll} className="text-xs text-navy font-semibold hover:underline flex items-center gap-1"><Check size={12} /> Mark all read</button>}
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {items.length === 0 && (
                <div className="p-8 text-center text-sm text-neutral-500">
                  <Bell size={24} className="mx-auto opacity-40 mb-2" />
                  You're all caught up
                </div>
              )}
              {items.map((n) => {
                const Ic = KIND_ICONS[n.kind] || Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => !n.read && markOne(n.id)}
                    className={`w-full flex gap-3 p-4 border-b last:border-0 text-left transition-colors hover:bg-black/[0.02] ${!n.read ? 'bg-navy/[0.04]' : ''}`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center ${!n.read ? 'bg-navy text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                      <Ic size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <p className="text-sm font-semibold">{n.title}</p>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-navy flex-shrink-0 mt-1.5"></span>}
                      </div>
                      <p className="text-xs text-neutral-600 mt-0.5 leading-relaxed">{n.body}</p>
                      <p className="text-[10px] text-neutral-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            {'Notification' in window && Notification.permission === 'default' && (
              <div className="p-3 border-t bg-navy/5 text-xs text-navy flex items-center justify-between">
                <span>Enable browser alerts?</span>
                <button onClick={() => Notification.requestPermission()} className="font-semibold hover:underline">Turn on</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
